import { useAlert } from '@shared/hooks/useAlert';
import { useForm } from 'react-hook-form';
import useModal from '../../../@shared/components/Modal/hooks/useModal';
import { useLocations } from '../../../hooks/collections/useLocations';
import { useLoader } from '../../../@shared/hooks/useLoader';
import Label from '../../../components/Label/Label';
import Input from '../../../shared/Input/Input';
import ButtonPrimary from '../../../shared/Button/ButtonPrimary';
import { useEffect, useRef, useState } from 'react';
import { useMapMarker } from '@core/components/useMapMarker';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import Select from '../../../shared/Select/Select';
import ButtonSecondary from '../../../shared/Button/ButtonSecondary';
import { ILocation } from '../../../@core/interfaces/ILocation';
import _ from 'lodash';

interface Props {
  location?: ILocation;
}

const containerStyle = {
  width: '100%',
  height: '400px'
};

const centerDefault = {
  lat: 40.73918719888581,
  lng: -73.99311915419923
};

const DAYS_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];

const generateTimeOptions = () => {
  return _.flatMap(_.range(24), hour => {
    const formattedHour = _.padStart(hour.toString(), 2, '0');
    return [
      { value: `${formattedHour}:00`, label: `${formattedHour}:00` },
      { value: `${formattedHour}:30`, label: `${formattedHour}:30` }
    ];
  });
};

const TIME_OPTIONS = generateTimeOptions();

const convertToTimeRanges = (times: string[]): { start: string, end: string }[] => {
  if (_.isEmpty(times)) return [];

  if (_.some(times, time => time.includes('-'))) {
    return _.chain(times)
      .filter(time => time.includes('-'))
      .map(timeRange => {
        const [start, end] = timeRange.split('-');
        return { start, end };
      })
      .value();
  }

  const sortedTimes = _.sortBy(times);

  if (_.isEmpty(sortedTimes)) return [];

  const ranges: { start: string, end: string }[] = [];
  let rangeStart = sortedTimes[0];
  let prevTime = sortedTimes[0];

  const isConsecutive = (prev: string, current: string): boolean => {
    const [prevHour, prevMinute] = prev.split(':').map(Number);
    const [currentHour, currentMinute] = current.split(':').map(Number);

    return (currentHour === prevHour && currentMinute === prevMinute + 30) ||
      (currentHour === prevHour + 1 && prevMinute === 30 && currentMinute === 0);
  };

  _.forEach(_.tail(sortedTimes), currentTime => {
    if (!isConsecutive(prevTime, currentTime)) {
      ranges.push({ start: rangeStart, end: prevTime });
      rangeStart = currentTime;
    }
    prevTime = currentTime;
  });

  ranges.push({ start: rangeStart, end: prevTime });

  return ranges;
};

const generateTimeSlots = (start: string, end: string): string[] => {
  const [startHour, startMinute] = start.split(':').map(Number);
  const [endHour, endMinute] = end.split(':').map(Number);

  const startTotalMinutes = startHour * 60 + startMinute;
  const endTotalMinutes = endHour * 60 + endMinute;

  const minuteValues = _.range(startTotalMinutes, endTotalMinutes + 1, 30);

  return _.map(minuteValues, minutes => {
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    return `${_.padStart(hour.toString(), 2, '0')}:${_.padStart(minute.toString(), 2, '0')}`;
  });
};

export const AccountLocationForm = ({ location }: Props) => {
  const modal = useModal();
  const { createLocation, updateLocation, getReference, deleteLocation } = useLocations();
  const alert = useAlert();
  const loader = useLoader();
  const { register, handleSubmit, setValue, watch, getValues } = useForm<any>({
    defaultValues: {
      name: location?.name || '',
      address: location?.address || '',
      coordinates: location?.coordinates || {
        lat: 40.73918719888581,
        lng: -73.99311915419923
      },
      openHours: location?.openHours || {
        0: [],
        1: [],
        2: [],
        3: [],
        4: [],
        5: [],
        6: []
      }
    }
  });

  const [selectedTimeRanges, setSelectedTimeRanges] = useState<Record<string, { start: string, end: string }[]>>({
    0: convertToTimeRanges(location?.openHours?.[0] || []),
    1: convertToTimeRanges(location?.openHours?.[1] || []),
    2: convertToTimeRanges(location?.openHours?.[2] || []),
    3: convertToTimeRanges(location?.openHours?.[3] || []),
    4: convertToTimeRanges(location?.openHours?.[4] || []),
    5: convertToTimeRanges(location?.openHours?.[5] || []),
    6: convertToTimeRanges(location?.openHours?.[6] || [])
  });

  useEffect(() => {
    const openHours = getValues('openHours');
    if (openHours) {
      const initialTimeRanges: Record<string, { start: string, end: string }[]> = {
        0: [],
        1: [],
        2: [],
        3: [],
        4: [],
        5: [],
        6: []
      };

      Object.keys(openHours).forEach(day => {
        if (Array.isArray(openHours[day])) {
          initialTimeRanges[day] = convertToTimeRanges([...openHours[day]]);
        }
      });

      setSelectedTimeRanges(initialTimeRanges);
    }
  }, [location]);

  const inputs = [
    {
      name: 'name',
      label: 'Name',
      type: 'text'
    }
  ];
  const mapRef = useRef<any>();
  const mapClicked = useRef(false);

  const handleMaps = (data) => {
    if (data?.coordinates) {
      setValue('coordinates', data.coordinates);
    }

    if (data?.formatted_address) {
      setValue('address', data.formatted_address);
    }
  };

  const [tempTimeRange, setTempTimeRange] = useState<Record<string, { start: string, end: string }>>({
    0: { start: '', end: '' },
    1: { start: '', end: '' },
    2: { start: '', end: '' },
    3: { start: '', end: '' },
    4: { start: '', end: '' },
    5: { start: '', end: '' },
    6: { start: '', end: '' }
  });

  const handleStartTimeChange = (day: string, time: string) => {
    setTempTimeRange(prev => _.set(_.cloneDeep(prev), `${day}.start`, time));
  };

  const handleEndTimeChange = (day: string, time: string) => {
    setTempTimeRange(prev => _.set(_.cloneDeep(prev), `${day}.end`, time));
  };

  const handleAddTimeRange = (day: string) => {
    const { start, end } = tempTimeRange[day];
    if (_.isEmpty(start) || _.isEmpty(end)) return;

    if (start >= end) {
      alert.error('Invalid Time Range', 'End time must be after start time');
      return;
    }

    const isOverlapping = _.some(selectedTimeRanges[day], range =>
      (start >= range.start && start < range.end) ||
      (end > range.start && end <= range.end) ||
      (start <= range.start && end >= range.end)
    );

    if (isOverlapping) {
      alert.error('Overlapping Time Range', 'This time range overlaps with an existing range');
      return;
    }

    const newTimeRanges = _.sortBy([...selectedTimeRanges[day], { start, end }], 'start');
    setSelectedTimeRanges(prev => _.set(_.cloneDeep(prev), day, newTimeRanges));
    const timeSlots = generateTimeSlots(start, end);
    const currentTimes = getValues(`openHours.${day}`) || [];
    const newTimes = _.sortBy(_.union(currentTimes, timeSlots));
    setValue(`openHours.${day}`, newTimes);
    setTempTimeRange(prev => _.set(_.cloneDeep(prev), day, { start: '', end: '' }));
  };

  const handleRemoveTimeRange = (day: string, index: number) => {
    const dayRanges = _.cloneDeep(selectedTimeRanges[day]);
    const removedRange = dayRanges[index];
    const newTimeRanges = _.filter(dayRanges, (_, i) => i !== index);
    setSelectedTimeRanges(prev => _.set(_.cloneDeep(prev), day, newTimeRanges));
    const removedTimeSlots = generateTimeSlots(removedRange.start, removedRange.end);
    const currentTimes = getValues(`openHours.${day}`) || [];
    const newTimes = _.difference(currentTimes, removedTimeSlots);
    setValue(`openHours.${day}`, newTimes);
  };

  const handleClonePreviousDay = (day: string) => {
    const dayIndex = Number(day);
    const previousDayIndex = dayIndex === 0 ? 6 : dayIndex - 1;
    const previousDayRanges = _.cloneDeep(selectedTimeRanges[previousDayIndex]);
    setSelectedTimeRanges(prev => _.set(_.cloneDeep(prev), day, previousDayRanges));
    const previousDayTimes = getValues(`openHours.${previousDayIndex}`) || [];
    setValue(`openHours.${day}`, _.clone(previousDayTimes));
    setTempTimeRange(prev => _.set(_.cloneDeep(prev), day, { start: '', end: '' }));
  };

  const { handleMarker } = useMapMarker(mapClicked, handleMaps);

  const submit = async (data) => {
    const isUpdate = !!location;
    const messageAlert = isUpdate ? 'Are you sure you want to update this location?' : 'Are you sure you want to create this location?';

    if (!await alert.confirm('Confirm', messageAlert)) return;

    try {
      loader.start();
      if (isUpdate) {
        await updateLocation(location.id, {
          ...data,
          company: getReference(`users/${location.company.id}`),
          active: true
        });
        alert.toastSuccess('Location updated successfully');
        return;
      }

      const LocationId = await createLocation(data);
      alert.toastSuccess('Location created successfully');
      modal.close({ LocationId });
    } catch (e) {
      console.error(e);
      alert.error('Error', 'An error occurred while processing the location');
    } finally {
      loader.stop();
      modal.close();
    }
  };

  const handleDeleteLocation = async () => {
    if (!await alert.confirm('Confirm', 'Are you sure you want to delete this location?')) return;
    await deleteLocation(location.id);
    modal.close();
  };

  return (
    <div className={'mt-4 flex gap-4 flex-col'}>
      {inputs.map((input) => (
        <div>
          <Label>{input.label}</Label>
          <Input
            type={input.type} className="mt-1.5" {...register(input.name, {
            required: true,
          })}/>
        </div>
      ))}

      <div>
        <Label>Detailed address</Label>
        <span className="block mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {watch('address')}
        </span>
        <div className="mt-4">
          <div className="aspect-w-5 aspect-h-5 sm:aspect-h-3">
            <div className="rounded-xl overflow-hidden">
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={location ? {
                  lat: location.coordinates.lat,
                  lng: location.coordinates.lng
                } : centerDefault}
                zoom={12}
                onClick={handleMarker}
                onLoad={(map) => {
                  mapRef.current = map;
                }}
              >
                {getValues('coordinates.lat') && getValues('coordinates.lng') && (
                  <MarkerF
                    position={{
                      lat: getValues('coordinates.lat'),
                      lng: getValues('coordinates.lng')
                    }}
                    draggable
                    onDragEnd={handleMarker}
                  />
                )}
              </GoogleMap>
            </div>
          </div>
        </div>
      </div>

      <div>
        <Label>Opening Hours</Label>
        <div className="mt-4 grid grid-cols-1 gap-6">
          {DAYS_OF_WEEK.map((day, index) => (
            <div key={index} className="border border-neutral-200 dark:border-neutral-700 rounded-xl p-4">
              <h3 className="text-lg font-medium">{day}</h3>

              <div className="mt-3 flex flex-wrap gap-2">
                {selectedTimeRanges[index].map((range, rangeIndex) => (
                  <div
                    key={rangeIndex}
                    className="flex items-center bg-primary-100 dark:bg-primary-800 rounded-full px-3 py-1"
                  >
                    <span className="text-sm">{range.start} - {range.end}</span>
                    <button
                      type="button"
                      className="ml-2 text-primary-700 dark:text-primary-300 hover:text-primary-900 dark:hover:text-primary-100"
                      onClick={() => handleRemoveTimeRange(index.toString(), rangeIndex)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-3 flex flex-col gap-2">
                <div className="flex items-end gap-2">
                  <div className="flex-grow">
                    <Label>Start Time</Label>
                    <Select
                      className="mt-1"
                      onChange={(e) => handleStartTimeChange(index.toString(), e.target.value)}
                      value={tempTimeRange[index].start}
                    >
                      <option value="">Select start time</option>
                      {TIME_OPTIONS.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div className="flex-grow">
                    <Label>End Time</Label>
                    <Select
                      className="mt-1"
                      onChange={(e) => handleEndTimeChange(index.toString(), e.target.value)}
                      value={tempTimeRange[index].end}
                    >
                      <option value="">Select end time</option>
                      {TIME_OPTIONS.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <ButtonPrimary
                      className="h-10"
                      onClick={() => handleAddTimeRange(index.toString())}
                      disabled={!tempTimeRange[index].start || !tempTimeRange[index].end}
                    >
                      Add
                    </ButtonPrimary>
                  </div>
                </div>

                <div className="flex justify-end mt-2">
                  <ButtonSecondary
                    className="text-sm"
                    onClick={() => handleClonePreviousDay(index.toString())}
                  >
                    Clone from {DAYS_OF_WEEK[index === 0 ? 6 : index - 1]}
                  </ButtonSecondary>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 sm:p-6 flex-shrink-0 flex items-center justify-end gap-3">
        {location && (<ButtonSecondary type={'button'} onClick={handleDeleteLocation}>Delete</ButtonSecondary>)}
        <ButtonPrimary onClick={handleSubmit(submit)}>Submit</ButtonPrimary>
      </div>
    </div>
  );
};
